import { FigureAxisDataDto, isFigureAxisDataDto } from "./FigureAxisDataDto";
import { FigureGroupDataDto, isFigureGroupDataDto } from "./FigureGroupDataDto";
import { FigureLegendDataDto, isFigureLegendDataDto } from "./FigureLegendDataDto";
import {
  FigureReferenceLineDataDto,
  isFigureReferenceLineDataDto,
} from "./FigureReferenceLineDataDto";
import {
  FigureRegressionLineDataDto,
  isFigureRegressionLineDataDto,
} from "./FigureRegressionLineDataDto";
import { FigureVarDataDto, isFigureVarDataDto } from "./FigureVarDataDto";

/**
 * Represents the complete data payload for a figure, including all axes, data groups, legend entries, and line data.
 */
export interface FigureDataDto extends Record<
  string,
  | string
  | FigureAxisDataDto[]
  | FigureGroupDataDto[]
  | FigureLegendDataDto[]
  | FigureReferenceLineDataDto[]
  | FigureRegressionLineDataDto[]
  | FigureVarDataDto[]
> {
  id: string;
  figureType: string;
  legendType: string;
  properties: FigureVarDataDto[];
  figureTypeProperties: FigureVarDataDto[];
  axes: FigureAxisDataDto[];
  dataGroups: FigureGroupDataDto[];
  legend: FigureLegendDataDto[];
  referenceLines: FigureReferenceLineDataDto[];
  regressionLines: FigureRegressionLineDataDto[];
}

/**
 * Type guard to check if an object is a FigureDataDto value.
 *
 * @param obj - The object to check.
 * @returns True when the value matches the full figure payload shape.
 */
export const isFigureDataDto = (obj: unknown): obj is FigureDataDto => {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }
  const chkObj = obj as FigureDataDto;
  if (typeof chkObj.id !== "string") {
    return false;
  }
  if (typeof chkObj.figureType !== "string") {
    return false;
  }
  if (typeof chkObj.legendType !== "string") {
    return false;
  }
  if (!Array.isArray(chkObj.properties)) {
    return false;
  }
  if (chkObj.properties.some((property) => !isFigureVarDataDto(property))) {
    return false;
  }
  if (!Array.isArray(chkObj.figureTypeProperties)) {
    return false;
  }
  if (chkObj.figureTypeProperties.some((property) => !isFigureVarDataDto(property))) {
    return false;
  }
  if (!Array.isArray(chkObj.axes)) {
    return false;
  }
  if (chkObj.axes.some((axis) => !isFigureAxisDataDto(axis))) {
    return false;
  }
  if (!Array.isArray(chkObj.dataGroups)) {
    return false;
  }
  if (chkObj.dataGroups.some((group) => !isFigureGroupDataDto(group))) {
    return false;
  }
  if (!Array.isArray(chkObj.legend)) {
    return false;
  }
  if (chkObj.legend.some((item) => !isFigureLegendDataDto(item))) {
    return false;
  }
  if (!Array.isArray(chkObj.referenceLines)) {
    return false;
  }
  if (chkObj.referenceLines.some((item) => !isFigureReferenceLineDataDto(item))) {
    return false;
  }
  if (!Array.isArray(chkObj.regressionLines)) {
    return false;
  }
  if (chkObj.regressionLines.some((line) => !isFigureRegressionLineDataDto(line))) {
    return false;
  }
  return true;
};
